import { useMemo, useRef, useState } from 'react';

import { Visibility } from '@mui/icons-material';
import { Box, IconButton, Typography } from '@mui/material';
import {
  MaterialReactTable,
  MRT_ColumnDef,
  MRT_RowVirtualizer,
  MRT_SortingState,
  useMaterialReactTable,
} from 'material-react-table';
import { MRT_Localization_PT_BR } from 'material-react-table/locales/pt-BR';
import { WalletFund } from 'src/types/wallet';
import { formatCNPJ } from 'src/utils/document';

type GuestViewFundsListProps = {
  walletFunds: WalletFund[];
  isLoading?: boolean;
  onViewFund?: (walletFund: WalletFund) => void;
};

export function GuestViewFundsList({
  walletFunds,
  isLoading = false,
  onViewFund,
}: GuestViewFundsListProps) {
  const [sorting, setSorting] = useState<MRT_SortingState>([]);
  const rowVirtualizerInstanceRef = useRef<MRT_RowVirtualizer>(null);

  const columns = useMemo<MRT_ColumnDef<WalletFund>[]>(
    () => [
      {
        accessorKey: 'operation.name',
        header: 'Nome da Operação',
        size: 300,
        Cell: ({ cell, row }) => {
          return (
            <Typography variant="body2">
              {row.original.operation?.name || '-'}
            </Typography>
          );
        },
      },
      {
        accessorKey: 'operation.cnpj',
        header: 'CNPJ',
        size: 180,
        Cell: ({ cell, row }) => {
          const cnpj = row.original.operation?.cnpj;
          const formattedCnpj = cnpj ? formatCNPJ(cnpj) : '-';
          return <Typography variant="body2">{formattedCnpj}</Typography>;
        },
      },
      {
        accessorKey: 'actions',
        header: '',
        size: 120,
        enableSorting: false,
        enableColumnFilter: false,
        enableColumnFilterModes: false,
        Cell: ({ row }) => {
          return (
            <Box display="flex" gap={1} justifyContent="center">
              {onViewFund && (
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => onViewFund(row.original)}
                  title="Visualizar">
                  <Visibility fontSize="small" />
                </IconButton>
              )}
            </Box>
          );
        },
      },
    ],
    [onViewFund],
  );

  const table = useMaterialReactTable({
    columns,
    data: walletFunds ?? [],
    enableBottomToolbar: true,
    enableGlobalFilterModes: true,
    enablePagination: true,
    enableRowNumbers: false,
    enableRowVirtualization: true,
    enableColumnActions: false,
    enableGlobalFilter: true,
    enableColumnDragging: false,
    enableDensityToggle: false,
    enableExpandAll: false,
    enableFullScreenToggle: false,
    enableEditing: false,
    enableHiding: false,
    enableTopToolbar: true,
    enableFilters: true,
    enableColumnFilterModes: true,
    enableFilterMatchHighlighting: true,
    enableStickyHeader: true,
    onSortingChange: setSorting,
    state: { isLoading, sorting },
    rowVirtualizerInstanceRef,
    rowVirtualizerOptions: { overscan: 5 },
    localization: MRT_Localization_PT_BR,
    muiTablePaperProps: {
      elevation: 0,
    },
    muiTableHeadRowProps: {
      sx: (theme) => ({
        backgroundColor: theme.palette.grey[200],
      }),
    },
    muiTopToolbarProps: ({ table }) => ({
      'aria-disabled': false,
      sx: {
        backgroundColor: '#FAFAFA',
        boxShadow: '0px 5px 6px 0px rgba(229, 229, 229, 0.50)',
        borderRadius: '3px 3px 0px 0px',
        borderTop: '1px solid #E0E0E0',
        borderRight: '1px solid #E0E0E0',
        borderLeft: '1px solid #E0E0E0',
      },
    }),
    muiTableContainerProps: {
      sx: (theme) => ({
        maxHeight: '500px',
        border: '1px solid #E0E0E0',

        '&::-webkit-scrollbar': {
          height: 10,
          width: 10,
          WebkitAppearance: 'none',
          border: '1px solid #E0E0E0',
        },
        '&::-webkit-scrollbar-thumb': {
          borderRadius: 5,
          backgroundColor: theme.palette.grey['800'],
        },
        userSelect: 'none',
        WebkitUserSelect: 'none',
        KhtmlUserSelect: 'none',
        MozUserSelect: 'none',
        msUserSelect: 'none',
        'o-user-select': 'none',
        '&::selection, &::-moz-selection': {
          background: 'none',
        },
      }),
    },
  });

  return (
    <Box>
      <MaterialReactTable table={table} />
    </Box>
  );
}
